import { ChangeEvent, useCallback, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import {
  deletePricebookPeriod,
  getPricebookPeriods,
  readPricebookPeriodRefusal,
} from "../../../../services";
import { PricebookPeriod } from "../../../../types/price-book.type";
import ConfirmModal from "../../../ConfirmModal";
import Skeleton from "../../../ReprintAuth/Skeleton";
import CataloguePagination from "../../shared/Pagination";
import { useSearchablePagedRows } from "../../shared/useSearchablePagedRows";
import { ALL_FILTER_VALUE, catalogueRowHeight } from "../../shared/constants";
import ExtendPeriodDialog, {
  ExtendPeriodTarget,
} from "../Periods/ExtendPeriodDialog";
import {
  PERIOD_STATUSES,
  PERIOD_STATUS_LABEL,
  formatPeriodRange,
  toPeriodStatus,
} from "../Periods/periodModel";
import PeriodRow from "./PeriodRow";

const COLUMNS: { label: string; width?: string }[] = [
  { label: "สินค้า" },
  { label: "ช่วงราคา", width: "16%" },
  { label: "ราคาต่อหน่วย", width: "9%" },
  { label: "ส่วนลด %", width: "8%" },
  { label: "รวม VAT", width: "8%" },
  { label: "ก่อน VAT", width: "8%" },
  { label: "หมายเหตุ", width: "14%" },
  { label: "สถานะ", width: "8%" },
];
const ACTION_COLUMN = { label: "การดำเนินการ", width: "10%" };
/** A product name over its code is the tallest cell. */
const ROW_CONTENT_HEIGHT = 40;
const ROW_HEIGHT = catalogueRowHeight(ROW_CONTENT_HEIGHT);
const STATUS_FIELD_WIDTH = 200;
const DELETE_FAILED = "ลบช่วงราคาไม่สำเร็จ กรุณาลองอีกครั้ง";

interface PeriodsTabProps {
  pricebookUuid: string;
  /** The book's `updated_at`: every period write moves it, so the list is read again. */
  updatedAt: string;
  /** May add, edit, extend and delete (a writer, on a book that takes periods). */
  canManage: boolean;
  /** Why nothing can be written right now (unsaved price edits); empty when writes are open. */
  blocker: string;
  onAdd: () => void;
  onEdit: (period: PricebookPeriod) => void;
  /** A period was extended or deleted here, so the book has to be read again. */
  onChanged: () => void;
}

/** ช่วงราคา: every period of the book, newest end first. An ended period is read-only. */
export default function PeriodsTab({
  pricebookUuid,
  updatedAt,
  canManage,
  blocker,
  onAdd,
  onEdit,
  onChanged,
}: PeriodsTabProps) {
  const [statusFilter, setStatusFilter] = useState<string>(ALL_FILTER_VALUE);
  const [extendTarget, setExtendTarget] = useState<ExtendPeriodTarget | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PricebookPeriod | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<string>("");

  const fetchPeriods = useCallback(
    async (page: number, limit: number) => {
      const {
        data: { data },
      } = await getPricebookPeriods(pricebookUuid, page, limit, {
        status: toPeriodStatus(statusFilter),
      });
      return data ?? { rows: [], count: 0 };
    },
    [pricebookUuid, statusFilter],
  );

  const { page, setPage, limit, setLimit, rows, totalCount, loading, reload } =
    useSearchablePagedRows<PricebookPeriod>({ open: true, fetchRows: fetchPeriods });

  // A write re-reads the list in place, so the filter and page the writer was on stay.
  const [loadedUpdatedAt, setLoadedUpdatedAt] = useState<string>(updatedAt);
  if (loadedUpdatedAt !== updatedAt) {
    setLoadedUpdatedAt(updatedAt);
    reload();
  }

  const columns = canManage ? [...COLUMNS, ACTION_COLUMN] : COLUMNS;
  const deleteDescription = deleteTarget
    ? `${deleteTarget.product_code} ${deleteTarget.product_name ?? ""} ช่วง ${formatPeriodRange(
        deleteTarget.start_date,
        deleteTarget.end_date,
      )} · หลังลบ วันในช่วงนี้จะขายที่ราคาหลัก`
    : "";

  const handleChangeStatus = (event: ChangeEvent<HTMLInputElement>) => {
    setStatusFilter(event.target.value);
    setPage(1);
  };

  const handleExtend = (period: PricebookPeriod) =>
    setExtendTarget({ pricebookUuid, period });
  const handleCloseExtend = () => setExtendTarget(null);
  const handleExtended = () => {
    setExtendTarget(null);
    onChanged();
  };

  const handleAskDelete = (period: PricebookPeriod) => {
    setDeleteError("");
    setDeleteTarget(period);
  };
  const handleCloseDelete = () => {
    if (!deleting) setDeleteTarget(null);
  };
  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError("");
    try {
      await deletePricebookPeriod(pricebookUuid, deleteTarget.uuid);
      setDeleteTarget(null);
      onChanged();
    } catch (error) {
      setDeleteError(readPricebookPeriodRefusal(error) ?? DELETE_FAILED);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Stack gap={2}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        flexWrap="wrap"
      >
        <TextField
          select
          size="small"
          label="สถานะ"
          value={statusFilter}
          onChange={handleChangeStatus}
          sx={{ width: STATUS_FIELD_WIDTH }}
        >
          <MenuItem value={ALL_FILTER_VALUE}>ทั้งหมด</MenuItem>
          {PERIOD_STATUSES.map((status) => (
            <MenuItem key={status} value={status}>
              {PERIOD_STATUS_LABEL[status]}
            </MenuItem>
          ))}
        </TextField>
        {canManage && (
          <Tooltip title={blocker}>
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                disabled={Boolean(blocker)}
                onClick={onAdd}
              >
                เพิ่มช่วงราคา
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>

      <TableContainer>
        <Table>
          <TableHead sx={{ bgcolor: (theme) => theme.palette.background.default }}>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.label} align="center" sx={{ width: column.width }}>
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <Skeleton columns={columns.length} rows={limit} rowHeight={ROW_HEIGHT} />
            )}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary" fontWeight={500}>
                    ยังไม่มีช่วงราคา
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              rows.map((period, index) => (
                <PeriodRow
                  key={period.uuid}
                  period={period}
                  striped={index % 2 === 1}
                  canManage={canManage}
                  blocker={blocker}
                  onEdit={onEdit}
                  onExtend={handleExtend}
                  onDelete={handleAskDelete}
                />
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <CataloguePagination
        totalCount={totalCount}
        page={page}
        setPage={setPage}
        limit={limit}
        setLimit={setLimit}
      />

      <ExtendPeriodDialog
        target={extendTarget}
        onClose={handleCloseExtend}
        onExtended={handleExtended}
      />
      <ConfirmModal
        open={Boolean(deleteTarget)}
        title="ลบช่วงราคานี้?"
        description={deleteDescription}
        confirmLabel="ลบช่วงราคา"
        confirmColor="error"
        loading={deleting}
        onClose={handleCloseDelete}
        onConfirm={handleConfirmDelete}
      >
        {deleteError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {deleteError}
          </Alert>
        )}
      </ConfirmModal>
    </Stack>
  );
}
